// Dispo Utility Functions

export type Rhythmus = '2er' | '3er';

export interface OrderDeliveryPair {
  orderDayOfWeek: number; // 0=Sonntag, 1=Montag, ..., 6=Samstag
  deliveryDayOfWeek: number;
  daysUntilDelivery: number;
}

export interface ScheduledOrder {
  orderDate: Date;
  deliveryDate: Date;
  orderDayName: string;
  deliveryDayName: string;
}

export interface DailyStockEntry {
  date: Date;
  dayName: string;
  isSunday: boolean;
  stockStart: number;
  dailyAV: number;
  delivery: number;
  simulatedOrder: number;
  stockEnd: number;
  isNegative: boolean;
  isDeliveryDay: boolean;
  isOrderDay: boolean;
}

export interface DispoLocalData {
  bestellmenge: number;
  wochenAV: number;
  bestand: number;
  dispoBestand: number;
  selectedDate: string;
}

const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const DAY_NAMES_FULL = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

// Rhythmus-Konfigurationen
// Bei 3er: Mo→Fr, Di→Mo, Do→Mi
// Bei 2er: Di→Mo, Fr→Do
const RHYTHM_CONFIG: Record<Rhythmus, OrderDeliveryPair[]> = {
  '3er': [
    { orderDayOfWeek: 1, deliveryDayOfWeek: 5, daysUntilDelivery: 4 },  // Mo → Fr (gleiche Woche)
    { orderDayOfWeek: 2, deliveryDayOfWeek: 1, daysUntilDelivery: 6 },  // Di → Mo (nächste Woche)
    { orderDayOfWeek: 4, deliveryDayOfWeek: 3, daysUntilDelivery: 6 },  // Do → Mi (nächste Woche)
  ],
  '2er': [
    { orderDayOfWeek: 2, deliveryDayOfWeek: 1, daysUntilDelivery: 6 },  // Di → Mo (nächste Woche)
    { orderDayOfWeek: 5, deliveryDayOfWeek: 4, daysUntilDelivery: 6 },  // Fr → Do (nächste Woche)
  ],
};

export function getDayName(date: Date): string {
  return DAY_NAMES[date.getDay()];
}

export function getDayNameFull(date: Date): string {
  return DAY_NAMES_FULL[date.getDay()];
}

export function formatDate(date: Date): string {
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
}

export function formatDateFull(date: Date): string {
  return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

export function isSunday(date: Date): boolean {
  return date.getDay() === 0;
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

// Liefertag-Paare für einen Rhythmus
export function getDeliveryDays(rhythmus: Rhythmus): OrderDeliveryPair[] {
  return RHYTHM_CONFIG[rhythmus] || RHYTHM_CONFIG['3er'];
}

// Prüft ob ein Tag ein Bestelltag ist
export function isOrderDay(date: Date, rhythmus: Rhythmus): boolean {
  const dayOfWeek = date.getDay();
  const config = getDeliveryDays(rhythmus);
  return config.some(pair => pair.orderDayOfWeek === dayOfWeek);
}

// Prüft ob ein Tag ein Liefertag ist
export function isDeliveryDay(date: Date, rhythmus: Rhythmus): boolean {
  const dayOfWeek = date.getDay();
  const config = getDeliveryDays(rhythmus);
  return config.some(pair => pair.deliveryDayOfWeek === dayOfWeek);
}

// Berechnet das Lieferdatum für einen Bestelltag
export function calculateDeliveryDate(orderDate: Date, rhythmus: Rhythmus): Date | null {
  const dayOfWeek = orderDate.getDay();
  const config = getDeliveryDays(rhythmus);
  const pair = config.find(p => p.orderDayOfWeek === dayOfWeek);

  if (!pair) return null;

  return addDays(orderDate, pair.daysUntilDelivery);
}

// Findet alle Bestellungen in einem Zeitraum
export function getOrdersInPeriod(
  startDate: Date,
  days: number,
  rhythmus: Rhythmus
): ScheduledOrder[] {
  const orders: ScheduledOrder[] = [];
  const config = getDeliveryDays(rhythmus);

  for (let i = 0; i < days; i++) {
    const currentDate = addDays(startDate, i);
    const dayOfWeek = currentDate.getDay();

    const pair = config.find(p => p.orderDayOfWeek === dayOfWeek);
    if (pair) {
      const deliveryDate = addDays(currentDate, pair.daysUntilDelivery);
      orders.push({
        orderDate: new Date(currentDate),
        deliveryDate,
        orderDayName: getDayName(currentDate),
        deliveryDayName: getDayName(deliveryDate),
      });
    }
  }

  return orders;
}

// Findet alle Lieferungen die in einen Zeitraum fallen (auch von vorherigen Bestellungen)
export function getDeliveriesInPeriod(
  startDate: Date,
  days: number,
  rhythmus: Rhythmus
): ScheduledOrder[] {
  // Wir müssen auch Bestellungen von vor dem Starttag berücksichtigen
  // Die maximale Vorlaufzeit ist 6 Tage, also schauen wir 7 Tage zurück
  const extendedStartDate = addDays(startDate, -7);
  const allOrders = getOrdersInPeriod(extendedStartDate, days + 7, rhythmus);

  const endDate = addDays(startDate, days);

  return allOrders.filter(order =>
    order.deliveryDate >= startDate && order.deliveryDate < endDate
  );
}

// Berechnet den Tages-AV (Wochen-AV / 6, da Sonntag nicht zählt)
export function calculateDailyAV(wochenAV: number): number {
  return Math.round((wochenAV / 6) * 100) / 100;
}

// Findet den nächsten Liefertag AB einem Datum (inklusiv)
export function getNextDeliveryDay(fromDate: Date, rhythmus: Rhythmus): Date {
  const config = getDeliveryDays(rhythmus);
  const deliveryDaysOfWeek = config.map(p => p.deliveryDayOfWeek);

  for (let i = 0; i < 14; i++) {
    const checkDate = addDays(fromDate, i);
    if (deliveryDaysOfWeek.includes(checkDate.getDay())) {
      return checkDate;
    }
  }
  return addDays(fromDate, 7); // Fallback
}

// Findet den nächsten Bestelltag AB einem Datum (inklusiv)
export function getNextOrderDay(fromDate: Date, rhythmus: Rhythmus): Date {
  const config = getDeliveryDays(rhythmus);
  const orderDaysOfWeek = config.map(p => p.orderDayOfWeek);

  for (let i = 0; i < 14; i++) {
    const checkDate = addDays(fromDate, i);
    if (orderDaysOfWeek.includes(checkDate.getDay())) {
      return checkDate;
    }
  }
  return addDays(fromDate, 7); // Fallback
}

// Berechnet die Anzahl Tage bis zum übernächsten Liefertag
// (nach der Lieferung der aktuellen Bestellung)
export function calculateSimulationDays(startDate: Date, rhythmus: Rhythmus): number {
  // 1. Finde den nächsten Bestelltag (oder heute wenn es einer ist)
  const nextOrderDay = getNextOrderDay(startDate, rhythmus);

  // 2. Berechne das Lieferdatum für diese Bestellung
  const deliveryDate = calculateDeliveryDate(nextOrderDay, rhythmus);
  if (!deliveryDate) return 10; // Fallback

  // 3. Finde den NÄCHSTEN Liefertag NACH diesem Lieferdatum
  const nextDeliveryAfter = getNextDeliveryDay(addDays(deliveryDate, 1), rhythmus);

  // 4. Berechne die Tage vom Start bis zu diesem Tag + 1 (für Abend/Übergang)
  const diffTime = nextDeliveryAfter.getTime() - startDate.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

  return Math.max(diffDays, 5); // Mindestens 5 Tage
}

// Generiert die 14-Tage-Auswahl ab heute
export function getDateOptions(days: number = 14): Date[] {
  const options: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < days; i++) {
    options.push(addDays(today, i));
  }

  return options;
}

// Berechnet den Bestandsverlauf über einen Zeitraum
// bestellmenge = die Menge die der User HEUTE bestellen würde (simuliert)
// offeneLieferung = bereits bestellte Menge die noch unterwegs ist (Dispo - Bestand)
export function calculateStockSimulation(
  startDate: Date,
  days: number,
  bestand: number,
  wochenAV: number,
  bestellmenge: number,
  rhythmus: Rhythmus,
  simulateOrderToday: boolean = true,
  offeneLieferung: number = 0
): DailyStockEntry[] {
  const entries: DailyStockEntry[] = [];
  const dailyAV = calculateDailyAV(wochenAV);

  // Berechne das Lieferdatum für die simulierte Bestellung
  // NUR wenn der Starttag ein Bestelltag ist
  let simulatedDeliveryDate: Date | null = null;
  if (simulateOrderToday && isOrderDay(startDate, rhythmus)) {
    simulatedDeliveryDate = calculateDeliveryDate(startDate, rhythmus);
  }

  // Finde den ersten Liefertag für die offene Lieferung
  // WICHTIG: Lieferungen am Startdatum selbst werden NICHT berücksichtigt
  // (diese kommen von vergangenen Bestellungen die nicht mehr relevant sind)
  const firstDeliveryDate = getNextDeliveryDay(addDays(startDate, 1), rhythmus);
  let offeneLieferungGebucht = false;

  let currentStock = bestand;

  for (let i = 0; i < days; i++) {
    const currentDate = addDays(startDate, i);
    const dateKey = currentDate.toISOString().split('T')[0];
    const sunday = isSunday(currentDate);
    const orderDay = isOrderDay(currentDate, rhythmus);
    const deliveryDay = isDeliveryDay(currentDate, rhythmus);

    // Wareneingang berechnen
    let wareneingang = 0;

    // 1. Offene Lieferung am ersten Liefertag einbuchen
    if (!offeneLieferungGebucht &&
        offeneLieferung > 0 &&
        firstDeliveryDate.toISOString().split('T')[0] === dateKey) {
      wareneingang += offeneLieferung;
      offeneLieferungGebucht = true;
    }

    // 2. Simulierte Bestellung: Lieferung kommt am berechneten Liefertag an
    if (simulatedDeliveryDate &&
        simulatedDeliveryDate.toISOString().split('T')[0] === dateKey) {
      wareneingang += bestellmenge;
    }

    const stockStart = currentStock;
    const todayAV = sunday ? 0 : dailyAV; // Sonntags kein Abverkauf
    const stockEnd = stockStart - todayAV + wareneingang;

    entries.push({
      date: currentDate,
      dayName: getDayName(currentDate),
      isSunday: sunday,
      stockStart: Math.round(stockStart * 100) / 100,
      dailyAV: todayAV,
      delivery: 0,
      simulatedOrder: wareneingang, // Jetzt enthält dies offene + simulierte Lieferung
      stockEnd: Math.round(stockEnd * 100) / 100,
      isNegative: stockEnd < 0,
      isDeliveryDay: deliveryDay,
      isOrderDay: orderDay,
    });

    currentStock = stockEnd;
  }

  return entries;
}

// LocalStorage Funktionen
const STORAGE_KEY = 'dispo_simulation_data';

export function saveToLocalStorage(data: DispoLocalData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('LocalStorage nicht verfügbar:', e);
  }
}

export function loadFromLocalStorage(): DispoLocalData | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('LocalStorage Lesefehler:', e);
  }
  return null;
}

export function getDefaultData(): DispoLocalData {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    bestellmenge: 100,
    wochenAV: 60,
    bestand: 50,
    dispoBestand: 50,
    selectedDate: today.toISOString(),
  };
}
